import { Injectable } from '@angular/core';
import { MatExpansionPanel } from '@angular/material/expansion';
import { Router } from '@angular/router';
import { FilterService, FilterState } from '@app/core/services/container/filter.service';
import { ParameterResolutionService } from '@app/core/services/parameter/parameter.service';
import { DataTableState } from '@components/data/data-table-new/state/state.model';
import { throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { Notification } from '../model/notification.model';
import { NotificationViewerTableData } from '../widgets/notification-viewer/notification-viewer.component.table';
import { NotificationService } from './notification.service';

@Injectable({
  providedIn: 'root'
})
export class NotificationCommandService {
  protected data: Notification[] = [];
  protected lastReceived: string;
  protected total = -1;
  protected maxNumOfNotifications: number;
  protected previousFilter: FilterState = { search: '', value: {} };
  protected loading: boolean;

  constructor(
    protected notificationService: NotificationService,
    protected notificationViewerTableData: NotificationViewerTableData,
    protected parameterResolutionService: ParameterResolutionService,
    protected filterService: FilterService,
    protected router: Router
  ) {
    this.maxNumOfNotifications = this.parameterResolutionService.resolveNumericValue('NOT_MAX_NOTIFICATIONS');
  }

  refreshData(state: DataTableState = this.notificationViewerTableData.getState(), newNotification?: Notification) {
    const newNotificationReceived = newNotification != null && newNotification.id !== this.lastReceived;
    this.loading = state.page.index === 0 && !newNotification;
    this.notificationViewerTableData.datasource.loading = state.page.index * state.page.size < this.total;
    if (!newNotificationReceived) {
      if (JSON.stringify(this.previousFilter) !== JSON.stringify(state.filter)) {
        this.refreshWithFilters(state);
      } else if (this.data.length !== this.total) {
        this.refresh(state);
      } else {
        this.loading = false;
        this.notificationViewerTableData.datasource.loading = false;
      }
    } else {
      this.newNotificationReceived(state, newNotification);
    }
  }

  protected refresh(state: DataTableState): void {
    let filters: string = this.filterService.getStateFilters(state.filter, FilterService.FILTER_OR);
    if (this.total < 0 || state.page.index * state.page.size < this.total) {
      this.notificationService
        .getAllPaginated(state.page, state.sort, state.filter.search, filters)
        .pipe(
          catchError(error => throwError(error)),
          tap(page => {
            this.total = page.totalElements;
            const currentNotificationsIds: string[] = this.notificationViewerTableData.datasource.data.map(
              notification => notification.id
            );
            const newNotifications: Notification[] = page.content.filter(
              notification => !currentNotificationsIds.includes(notification.id)
            );
            this.data.push(...newNotifications);
            this.notificationViewerTableData.datasource.setData(this.data);
            this.loading = false;
            this.notificationViewerTableData.datasource.loading = false;
          })
        )
        .subscribe();
    } else {
      this.loading = false;
      this.notificationViewerTableData.datasource.loading = false;
    }
  }

  protected refreshWithFilters(state: DataTableState): void {
    this.previousFilter = { ...state.filter };
    let filters: string = this.filterService.getStateFilters(state.filter, FilterService.FILTER_OR);
    state.page.index = 0;
    this.notificationService
      .getAllPaginated(state.page, state.sort, state.filter.search, filters)
      .pipe(
        catchError(error => throwError(error)),
        tap(page => {
          this.total = page.totalElements;
          this.data = page.content;
          this.notificationViewerTableData.datasource.setData(this.data);
          this.loading = false;
          this.notificationViewerTableData.datasource.loading = false;
        })
      )
      .subscribe();
  }

  newNotificationReceived(state: DataTableState, notification?: Notification): void {
    const appliedFilters: string[] = Object.keys(state.filter.value)
      .filter(key => state.filter.value[key].value)
      .map(key => key);
    if (
      appliedFilters.length === 0 ||
      appliedFilters.includes(notification.category.toLowerCase()) ||
      appliedFilters.includes('unread') ||
      (notification.important && appliedFilters.includes('important'))
    ) {
      if (this.notificationViewerTableData.datasource.data.length > this.maxNumOfNotifications) {
        this.notificationViewerTableData.datasource.data.pop();
      }
      this.notificationViewerTableData.datasource.data.unshift(notification);
      this.total += 1;
      this.notificationViewerTableData.datasource.loading = false;
      this.lastReceived = notification.id;
    }
  }

  markAllAsRead(): void {
    this.loading = true;
    this.notificationService
      .markAllAsRead()
      .pipe(
        tap(_ => {
          this.notificationViewerTableData.datasource.data.forEach(notification => (notification.unread = false));
          this.notificationService.setNumUnreadNotifications(0);
          this.loading = false;
        })
      )
      .subscribe();
  }

  markSelectedAsRead(read: boolean): void {
    const ids: string[] = this.notificationViewerTableData.selection.selected
      .filter(notification => notification.unread === read)
      .map(notification => notification.id);

    this.notificationService
      .markListAsRead(ids, read)
      .pipe(
        tap(_ => {
          ids.forEach((id: string) => {
            const notification: Notification = this.notificationViewerTableData.datasource.data.find(n => n.id === id);
            if (notification) {
              notification.unread = !read;
            }
          });

          if (read) {
            this.notificationService.reduceNumUnreadNotifications(ids.length);
          } else {
            this.notificationService.updateNumUnreadNotifications(ids.length);
          }
        })
      )
      .subscribe();
  }

  markAsRead(notification: Notification, read: boolean, panel?: MatExpansionPanel) {
    this.notificationService
      .markAsRead(notification.id, read)
      .pipe(
        catchError(error => throwError(error)),
        tap(_ => {
          notification.unread = !read;
          read
            ? this.notificationService.reduceNumUnreadNotifications(1)
            : this.notificationService.updateNumUnreadNotifications(1);
          if (panel) {
            panel.toggle();
          }
        })
      )
      .subscribe();
  }

  markSelectedAsFlagged(flagged: boolean): void {
    const ids: string[] = this.notificationViewerTableData.selection.selected.map(notification => notification.id);

    this.notificationService
      .markListAsFlagged(ids, flagged)
      .pipe(
        tap(_ => {
          ids.forEach((id: string) => {
            const notification: Notification = this.notificationViewerTableData.datasource.data.find(n => n.id === id);
            notification.flagged = flagged;
          });
        })
      )
      .subscribe();
  }

  markAsFlagged(notification: Notification) {
    notification.flagged = !notification.flagged;
    this.notificationService.markAsFlagged(notification.id, notification.flagged).subscribe();
  }

  delete(id: string) {
    const index = this.notificationViewerTableData.datasource.data.map(notification => notification.id).indexOf(id);
    this.notificationViewerTableData.datasource.data.splice(index, 1);
  }

  deleteById(id: string): void {
    this.notificationService
      .delete(id)
      .pipe(
        tap(_ => {
          this.delete(id);
          const state: DataTableState = this.notificationViewerTableData.getState();
          this.notificationService.reduceNumUnreadNotifications(1);
          if (this.notificationViewerTableData.datasource.data.length < state.page.size) {
            this.refreshData();
          }
        })
      )
      .subscribe();
  }

  deleteSelected(): void {
    const ids: string[] = this.notificationViewerTableData.selection.selected.map(notification => notification.id);
    this.notificationService
      .deleteList(ids)
      .pipe(
        tap(_ => {
          ids.forEach(id => this.delete(id));
          this.notificationService.reduceNumUnreadNotifications(ids.length);
          this.refreshData();
        })
      )
      .subscribe();
  }

  isLoading(): boolean {
    return this.loading;
  }

  clear() {
    this.data = [];
    this.total = -1;
    this.previousFilter = { search: '', value: {} };
    this.notificationViewerTableData.clear();
  }
}
